/**
 * Free programs for My Lists "Send a Message" (BulkMessagingModal).
 * Pure helpers. The program rules come from the game plan picker
 * (gameplan/emailPrograms.ts) so both pickers count the same way.
 */
import {
  HOUSE_SCHOOL_IDS,
  ProgramRow,
  UnlockState,
  costsProgram,
} from './gameplan/emailPrograms';

export type Channel = 'email' | 'tweet';

export interface ChannelCoach {
  production_id?: number | string | null;
  coach_name?: string | null;
  email?: string | null;
  twitter_handle?: string | null;
  has_email?: boolean;
  has_twitter_handle?: boolean;
}

export interface ChannelSchool<C extends ChannelCoach = ChannelCoach> {
  school_id: number;
  name: string;
  logo_url?: string | null;
  coaching_staff?: C[] | null;
}

export function isHouseSchool(schoolId: number | string): boolean {
  return HOUSE_SCHOOL_IDS.includes(Number(schoolId));
}

/** True when this coach's contact for the channel came back in full. */
export function contactShown(c: ChannelCoach | null | undefined, channel: Channel): boolean {
  if (!c) return false;
  const v = channel === 'email' ? c.email : c.twitter_handle;
  return !!(v && String(v).trim());
}

/** True when the coach has this contact on file, shown or hidden. */
export function contactOnFile(c: ChannelCoach | null | undefined, channel: Channel): boolean {
  if (!c) return false;
  if (contactShown(c, channel)) return true;
  return channel === 'email' ? c.has_email === true : c.has_twitter_handle === true;
}

/**
 * Coaches at a school the picker can count for this channel. A paid family
 * sees every contact, so only shown ones count. A free family counts hidden
 * ones too, since a free program shows them. House schools never use a
 * program, so they count only what is shown.
 */
export function offeredCoaches<C extends ChannelCoach>(
  school: ChannelSchool<C>,
  channel: Channel,
  isPaid: boolean,
): C[] {
  const staff = school.coaching_staff || [];
  const showOnly = isPaid || isHouseSchool(school.school_id);
  return staff.filter((c) => (showOnly ? contactShown(c, channel) : contactOnFile(c, channel)));
}

/**
 * The unlock state with the house schools treated as held, so they never
 * read as a new program.
 */
export function withHouseSchools(state: UnlockState): UnlockState {
  if (state.isPaid) return state;
  const unlocked = new Set(state.unlocked);
  HOUSE_SCHOOL_IDS.forEach((id) => unlocked.add(String(id)));
  return { ...state, unlocked };
}

/** Picker rows in the shape the game plan helpers take. */
export function toProgramRows(
  schools: ChannelSchool[],
  channel: Channel,
  isPaid: boolean,
): ProgramRow[] {
  return schools.map((s) => ({
    school_id: s.school_id,
    name: s.name,
    logo_url: s.logo_url || null,
    emailCount: offeredCoaches(s, channel, isPaid).length,
    staff: (s.coaching_staff || []) as ProgramRow['staff'],
  }));
}

/**
 * What a picked school needs before the coaches step.
 * 'listed': the lists call already sent every contact.
 * 'unlock': ask the server. Spends a program only when the school is new.
 * A house school is never sent to the server, since that would spend one.
 */
export function fetchPlan(
  school: ChannelSchool,
  channel: Channel,
  state: UnlockState,
): 'listed' | 'unlock' {
  if (isHouseSchool(school.school_id)) return 'listed';
  if (costsProgram(school.school_id, state)) return 'unlock';
  const staff = school.coaching_staff || [];
  const hidden = staff.some((c) => contactOnFile(c, channel) && !contactShown(c, channel));
  return hidden ? 'unlock' : 'listed';
}

/**
 * Picked schools that use a free program. Counts every pick, also one the
 * current list or channel does not show.
 */
export function newPickCount(selected: Set<number>, state: UnlockState): number {
  if (state.isPaid) return 0;
  return Array.from(selected).filter((id) => costsProgram(id, state)).length;
}

/** True when one more new school fits the free programs left. */
export function canPickNewSchool(selected: Set<number>, state: UnlockState): boolean {
  if (state.isPaid) return true;
  return newPickCount(selected, state) < Math.max(0, state.remaining);
}

/** The picks the picker shows right now. */
export function shownPicks(selected: Set<number>, shownIds: number[]): Set<number> {
  const shown = new Set(shownIds);
  return new Set(Array.from(selected).filter((id) => shown.has(id)));
}

/** The short note on a school row. Null for a paid family. */
export function rowNote(
  schoolId: number,
  picked: boolean,
  canPickMore: boolean,
  state: UnlockState,
): { text: string; locked: boolean } | null {
  if (state.isPaid) return null;
  if (!costsProgram(schoolId, state)) return { text: 'Free to use', locked: false };
  if (picked || canPickMore) return { text: 'Uses 1 free program', locked: false };
  if (state.remaining <= 0) return { text: 'Needs a plan', locked: true };
  return { text: 'Unpick a school to swap', locked: false };
}
